import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { openDownload } from "@/lib/downloads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Truck, Warehouse, User as UserIcon, ArrowRightLeft, FileDown,
  Printer, Loader2, PackageSearch, QrCode, History, Trash2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const LOC_CONFIG = {
  storage: { color: "bg-slate-800 border-slate-700 text-slate-300", icon: Warehouse, label: "Storage" },
  van: { color: "bg-amber-500/10 border-amber-500/30 text-amber-300", icon: Truck, label: "Van" },
  technician: { color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", icon: UserIcon, label: "Technician" },
  initial: { color: "bg-slate-800 border-slate-700 text-slate-400", icon: PackageSearch, label: "Initial" },
};

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moveOpen, setMoveOpen] = useState(false);
  const [tokenVisible, setTokenVisible] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/equipment/${id}`);
      setItem(r.data);
    } catch (e) {
      toast.error("Equipment not found");
      navigate("/equipment");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const archive = async () => {
    if (!window.confirm("Archive this equipment? You can no longer track it after this.")) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Equipment archived");
      navigate("/equipment");
    } catch { toast.error("Could not archive"); }
  };

  if (loading || !item) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="equipment-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  const loc = item.location || "storage";
  const locConf = LOC_CONFIG[loc] || LOC_CONFIG.storage;
  const LocIcon = locConf.icon;
  const token = localStorage.getItem("trueline_token") || "";
  const tokenQs = token ? `?token=${encodeURIComponent(token)}` : "";
  const qrUrl = `${API}/equipment/${item.id}/qr.png${tokenQs}`;

  return (
    <div className="space-y-6" data-testid="equipment-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/equipment" className="text-slate-400 hover:text-blue-300" data-testid="back-to-equipment">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{item.brand} · {item.category}</div>
            <h1 className="text-2xl font-bold text-white">{item.name}</h1>
            <div className="text-xs text-slate-400 font-mono mt-1">{item.part_number}{item.serial_number ? ` · SN ${item.serial_number}` : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${locConf.color} flex items-center gap-1 px-3 py-1.5 text-sm`} data-testid="current-location-badge">
            <LocIcon className="w-3.5 h-3.5" /> {locConf.label}{loc === "technician" && item.technician_name ? `: ${item.technician_name}` : ""}
          </Badge>
          <Button onClick={() => setMoveOpen(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="move-equipment-btn">
            <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer / Checkout
          </Button>
          <Button variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" onClick={archive} data-testid="archive-equipment-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="equipment-info-card">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Equipment Info</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Info label="Part Number" value={item.part_number} mono />
              <Info label="Serial Number" value={item.serial_number || "—"} mono />
              <Info label="Brand" value={item.brand || "—"} />
              <Info label="Model" value={item.model || "—"} />
              <Info label="Category" value={item.category || "—"} />
              <Info label="Added" value={item.created_at ? item.created_at.slice(0, 10) : "—"} />
              <Info label="Last Updated" value={item.updated_at ? item.updated_at.slice(0, 10) : "—"} />
            </dl>
            {item.notes && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Notes</div>
                <p className="text-sm text-slate-300 whitespace-pre-line">{item.notes}</p>
              </div>
            )}
          </div>

          {/* Movement History */}
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="movement-history">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                <History className="w-4 h-4 text-blue-400" /> Movement History
              </div>
              <Badge className="bg-slate-800 border border-slate-700 text-slate-400 text-[10px]">{(item.history || []).length} events</Badge>
            </div>
            <div className="space-y-2.5">
              {(item.history || []).length === 0 ? (
                <p className="text-sm text-slate-500 italic">No history yet.</p>
              ) : item.history.map((mv) => <MovementRow key={mv.id} mv={mv} equipmentId={item.id} onSigned={load} />)}
            </div>
          </div>
        </div>

        {/* QR Card */}
        <div className="space-y-4">
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5 text-center" data-testid="qr-card">
            <div className="text-[10px] uppercase tracking-widest text-blue-300 font-bold mb-3 flex items-center justify-center gap-1.5">
              <QrCode className="w-3.5 h-3.5" /> Scan with phone camera
            </div>
            <div className="bg-white rounded-lg p-3 inline-block">
              <img src={qrUrl} alt="QR code" className="w-44 h-44" data-testid="qr-image" />
            </div>
            <div className="mt-3 text-xs text-slate-400">
              QR Token: <button
                onClick={() => { navigator.clipboard.writeText(item.qr_token); toast.success("Copied"); setTokenVisible(true); }}
                className="font-mono text-blue-300 hover:text-blue-200 underline"
                data-testid="qr-token-btn">
                {tokenVisible ? item.qr_token : "•••••••• (click to copy)"}
              </button>
            </div>
            <a
              href={qrUrl}
              download={`qr-${item.part_number}.png`}
              className="mt-3 inline-flex items-center justify-center gap-1.5 w-full text-xs uppercase tracking-wider px-3 py-2 rounded-md border border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
              data-testid="qr-download-btn"
            >
              <FileDown className="w-3.5 h-3.5" /> Download QR PNG
            </a>
            <button
              onClick={() => window.print()}
              className="mt-2 inline-flex items-center justify-center gap-1.5 w-full text-xs uppercase tracking-wider px-3 py-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="qr-print-btn"
            >
              <Printer className="w-3.5 h-3.5" /> Print Label
            </button>
          </div>
        </div>
      </div>

      <MoveDialog
        key={`move-${moveOpen ? "o" : "c"}`}
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        equipment={item}
        onMoved={(formUrl) => {
          setMoveOpen(false);
          load();
          if (formUrl) {
            // Iter 47: use short-lived ticket, don't embed JWT in URL
            const path = formUrl.replace(/^\/api/, "/api");
            const m = formUrl.match(/equipment\/([^/]+)\/movements\/([^/]+)/);
            if (m) {
              openDownload(path, "equipment_detail_pdf", `${m[1]}:mv:${m[2]}`);
            }
          }
        }}
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</dt>
      <dd className={`text-sm text-slate-200 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}

function MovementRow({ mv, equipmentId, onSigned }) {
  const from = LOC_CONFIG[mv.from_location] || { color: "bg-slate-800 text-slate-300", icon: PackageSearch, label: mv.from_location };
  const to = LOC_CONFIG[mv.to_location] || { color: "bg-slate-800 text-slate-300", icon: PackageSearch, label: mv.to_location };
  const FromIcon = from.icon;
  const ToIcon = to.icon;

  const openForm = () =>
    openDownload(
      `/api/equipment/${equipmentId}/movements/${mv.id}/form.pdf`,
      "equipment_detail_pdf",
      `${equipmentId}:mv:${mv.id}`,
    );

  const markSigned = async () => {
    try {
      await api.post(`/equipment/${equipmentId}/movements/${mv.id}/sign`, {
        signed_by: mv.to_technician_name || "Receiver",
      });
      toast.success("Marked as signed");
      onSigned?.();
    } catch { toast.error("Failed to mark signed"); }
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-3" data-testid={`movement-${mv.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Badge className={`${from.color} text-[10px] flex items-center gap-1`}>
            <FromIcon className="w-3 h-3" /> {mv.from_location === "technician" ? (mv.from_technician_name || "Tech") : from.label}
          </Badge>
          <ArrowRightLeft className="w-3 h-3 text-slate-500" />
          <Badge className={`${to.color} text-[10px] flex items-center gap-1`}>
            <ToIcon className="w-3 h-3" /> {mv.to_location === "technician" ? (mv.to_technician_name || "Tech") : to.label}
          </Badge>
          <span className="text-slate-500">·</span>
          <span className="text-slate-400 text-[11px] uppercase tracking-wider">{(mv.reason || "").replace(/_/g, " ")}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {mv.signed ? (
            <Badge className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Signed
            </Badge>
          ) : (
            mv.reason !== "initial_registration" && (
              <button
                onClick={markSigned}
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1"
                data-testid={`mark-signed-${mv.id}`}
              >
                <AlertTriangle className="w-3 h-3" /> Mark Signed
              </button>
            )
          )}
          {mv.reason !== "initial_registration" && (
            <button
              type="button"
              onClick={openForm}
              className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-blue-500/30 text-blue-300 hover:bg-blue-500/10 flex items-center gap-1"
              data-testid={`movement-form-${mv.id}`}
            >
              <Printer className="w-3 h-3" /> Form
            </button>
          )}
        </div>
      </div>
      {mv.notes && <p className="text-xs text-slate-400 mt-2 italic">{mv.notes}</p>}
      <div className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-3">
        <span>{mv.created_at ? mv.created_at.slice(0, 19).replace("T", " ") : ""} UTC</span>
        <span>by {mv.created_by || "system"}</span>
      </div>
    </div>
  );
}

function MoveDialog({ open, onClose, equipment, onMoved }) {
  const [to, setTo] = useState(equipment?.location === "storage" ? "van" : "storage");
  const [technicianName, setTechnicianName] = useState("");
  const [technicianId, setTechnicianId] = useState("");
  const [reason, setReason] = useState("checkout");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    if (open) api.get("/master/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, [open]);

  const save = async () => {
    if (to === "technician" && !technicianName) {
      toast.error("Pick a technician");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/equipment/${equipment.id}/movements`, {
        to_location: to,
        to_technician_id: technicianId || null,
        to_technician_name: technicianName || null,
        reason, notes: notes.trim() || null,
      });
      toast.success("Equipment moved · custody form ready to print");
      onMoved?.(r.data.form_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[#0B1120] border-slate-800" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-blue-400" /> Transfer Equipment</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Currently at <span className="text-slate-300 font-medium">{equipment.location_label}</span>. After save, a signature form will open automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Move To *</Label>
            <select value={to} onChange={(e) => setTo(e.target.value)}
              className="mt-1.5 h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
              data-testid="move-to-location">
              <option value="storage">Storage</option>
              <option value="van">Van</option>
              <option value="technician">Technician</option>
            </select>
          </div>
          {to === "technician" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Technician *</Label>
              <select value={technicianName}
                onChange={(e) => {
                  const name = e.target.value;
                  setTechnicianName(name);
                  const t = technicians.find((x) => x.name === name);
                  setTechnicianId(t?.id || "");
                }}
                className="mt-1.5 h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
                data-testid="move-to-technician">
                <option value="">— Select —</option>
                {technicians.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Reason</Label>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-1.5 h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
              data-testid="move-reason">
              <option value="checkout">Checkout (taking out)</option>
              <option value="return">Return</option>
              <option value="transfer">Transfer between locations</option>
              <option value="service">Sent for service/repair</option>
              <option value="lost">Reported lost</option>
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5 bg-[#030712] border-slate-800 text-sm min-h-[60px]"
              placeholder="Customer / job context, condition on transfer, etc."
              data-testid="move-notes" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="move-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />}
            Save & Print Form
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
